<template>
  <div ref="svg"></div>
</template>

<script>
import { calculateHeights } from '@/utils/utils';

export default {
  name: 'ShaftImageBuildup',
  props: ['shaft', 'interactive', 'showLabels', 'height'],
  data: () => ({
    canvas: undefined,
    pattern: undefined,
    labelOffset: -200 + 100 - 25, // - image width + half image - half label container
  }),
  watch: {
    shaft() {
      this.redraw();
    },
  },
  mounted() {
    this.$nextTick(function() {
      if (this.shaft) this.redraw();
    });
  },
  methods: {
    async redraw() {
      if (this.shaft) {
        var svg = await this.openReferenceImage();
        this.createDraw(svg);
      }
    },
    openReferenceImage() {
      return new Promise(function(resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('get', require('@/assets/images/shaft_buildup.svg'), true);
        xhr.onreadystatechange = function() {
          if (xhr.readyState != 4) return;
          var status = xhr.status;
          if (status == 200) {
            var svg = xhr.responseXML.documentElement;
            svg = document.importNode(svg, true); // surprisingly optional in these browsers
            resolve(svg);
          } else {
            reject(status);
          }
        };
        xhr.send();
      });
    },
    createDraw(svg) {
      var heights = calculateHeights(this.shaft);
      //console.log(heights);

      if (!this.$refs.svg) return;
      if (this.canvas && this.$refs.svg)
        this.$refs.svg.removeChild(this.$refs.svg.firstChild);

      this.canvas = this.$svg(svg)
        .addTo(this.$refs.svg)
        .attr('height', null)
        .attr('width', null)
        .attr('viewBox', '0 0 200 ' + (heights.max + 2)) // add some space to ensure that lines are fully rendered
        .attr('preserveAspectRatio', 'xMinYMin slice')
        .attr('style', 'width:100%');
      if (this.height) this.canvas.attr('style', 'height:' + this.height);

      this.$emit(
        'heightChanged',
        this.$refs.svg.getBoundingClientRect().height,
      );

      this.pattern = this.canvas.pattern(5, 5, function(add) {
        add.rect(5, 5);
        add.line(0, 0, 5, 5).addClass('st5');
      });

      this.drawDimensioning(heights);
      this.drawWarningArea(heights);
      this.drawBase(heights);
      this.drawCover(heights);
      this.drawCone(heights);
      this.drawAdd(heights);
      this.drawSole(heights, this.shaft.input_channel.diameter.dn);
      this.drawRings(heights);

      this.$emit('svgLoaded', this.canvas.svg());
    },
    drawWarningArea(heights) {
      if (heights.shaft > heights.terrain) {
        this.canvas
          .rect(200, heights.shaft - heights.terrain)
          .attr('fill', 'red')
          .attr('opacity', '0.5')
          .stroke({ color: 'red', width: 2, linecap: 'round' })
          .back();
      }
    },
    drawBase(heights) {
      this.canvas
        .rect(100, heights.base)
        .addClass('st6')
        .fill(this.pattern)
        .translate(55, heights.max - heights.base);

      // place base label
      var baseLabel = this.canvas.findOne('#labelLocked').clone();
      if (this.showLabels) {
        baseLabel
          .children()[1]
          .attr(
            'style',
            'font-family:"Source Sans Pro", sans-serif; font-size:7.4976px;',
          );
        baseLabel.children()[1].text(heights.base + ' cm');
        baseLabel.translate(
          this.labelOffset,
          heights.max - heights.base / 2 - 10,
        );
        this.canvas.root().add(baseLabel);
      }
    },
    drawCover(heights) {
      this.canvas
        .rect(70, heights.cover)
        .fill(this.pattern)
        .translate(
          55,
          heights.max -
            heights.base -
            heights.buildups -
            heights.sole -
            heights.cone -
            heights.cover,
        );
    },
    drawCone(heights) {
      if (
        this.shaft.cone &&
        this.shaft.cone.type.indexOf('Flachabdeckung') >= 0
      ) {
        this.canvas
          .polygon(
            `0,${heights.cone} 100,${heights.cone}
            100,${heights.cone - 5} 70,${heights.cone - 5}
            70,0 0,0`,
          )
          .addClass('st6')
          .translate(
            55,
            heights.max -
              heights.base -
              heights.buildups -
              heights.sole -
              heights.cone,
          );
      } else {
        this.canvas
          .polygon(`0,${heights.cone} 100,${heights.cone} 70,0 0,0`)
          .addClass('st6')
          .translate(
            55,
            heights.max -
              heights.base -
              heights.buildups -
              heights.sole -
              heights.cone,
          );
      }

      // place cone label
      var topLabel = this.canvas.findOne('#labelEdit').clone();
      if (this.showLabels) {
        topLabel
          .children()[1]
          .attr(
            'style',
            'font-family:"Source Sans Pro", sans-serif; font-size:7.4976px;',
          );
        topLabel.children()[1].text(heights.cone + ' cm');
        topLabel.children()[1].fill('white');
        topLabel.translate(
          this.labelOffset,
          heights.max -
            heights.base -
            heights.buildups -
            heights.sole -
            heights.cone / 2 -
            10,
        );
        if (this.interactive) {
          topLabel.click(
            function() {
              this.$emit('editConeClick');
            }.bind(this),
          );
        } else {
          topLabel.children()[2].remove();
        }
        this.canvas.root().add(topLabel);
      }
    },
    drawAdd(heights) {
      var addButton = this.canvas.findOne('#buttonAdd');
      if (this.interactive) {
        addButton.translate(
          0,
          heights.max - heights.base - heights.sole - heights.buildups - 10,
        );
        addButton.click(
          function() {
            this.$emit('addClick');
          }.bind(this),
        );
      } else addButton.remove();
    },
    drawSole(heights, dn) {
      var bottom = heights.max - heights.base;
      var top = bottom - heights.sole;
      this.canvas
        .rect(100, heights.sole)
        .addClass('st6')
        .translate(55, top);

      if (!dn) dn = 150;
      dn = dn / 10; // convert to cm

      // place polyline
      this.canvas
        .polyline(
          `55,${top} ${105 - dn / 2},${bottom - dn / 2} ${105 +
            dn / 2},${bottom - dn / 2} ${55 + 100},${top}`,
        )
        .addClass('st8');

      // place channel
      this.canvas
        .circle(dn)
        .addClass('st8')
        .translate(105 - dn / 2, bottom - dn);

      // place sole label
      var soleLabel = this.canvas.findOne('#labelLocked').clone();
      if (this.showLabels) {
        soleLabel
          .children()[1]
          .attr(
            'style',
            'font-family:"Source Sans Pro", sans-serif; font-size:7.4976px;',
          );
        soleLabel.children()[1].text(heights.sole + ' cm');
        soleLabel.children()[1].text(heights.sole + ' cm');
        soleLabel.translate(this.labelOffset, bottom - heights.sole / 2 - 10);
        this.canvas.root().add(soleLabel);
      }
    },
    drawRings(heights) {
      var buildupOffset = heights.sole;
      this.shaft.buildups.forEach(buildup => {
        if (buildup.type) {
          let height = parseInt(
            buildup.type ? buildup.type.height : buildup.height,
          );

          // place box
          this.canvas
            .rect(100, height)
            .addClass('st5')
            .translate(55, heights.max - heights.base - height - buildupOffset);

          // place label
          if (this.showLabels) {
            let labelEdit = this.canvas.findOne('#labelEdit').clone();
            labelEdit
              .children()[1]
              .attr(
                'style',
                'font-family:"Source Sans Pro", sans-serif; font-size:7.4976px;',
              );
            labelEdit.children()[1].text(height + ' cm');
            if (this.interactive) {
              labelEdit.click(
                function() {
                  this.$emit('editBuildupClick', buildup);
                }.bind(this),
              );
            } else {
              labelEdit.children()[2].remove();
            }
            labelEdit.translate(
              this.labelOffset,
              heights.max - heights.base - (height / 2 + 10) - buildupOffset,
            );
            this.canvas.root().add(labelEdit);
          }

          buildupOffset += height;
        }
      });
    },
    drawDimensioning(heights) {
      // draw gok
      this.canvas
        .line(
          0,
          heights.max - heights.base - heights.terrain,
          200,
          heights.max - heights.base - heights.terrain,
        )
        .stroke({ color: 'red', width: 2, linecap: 'round' })
        .back();

      // draw horizontal top line
      this.canvas
        .line(
          20,
          heights.max - heights.base - heights.shaft,
          200,
          heights.max - heights.base - heights.shaft,
        )
        .addClass('st7');

      // draw horizontal bottom line
      this.canvas
        .line(0, heights.max - heights.base, 200, heights.max - heights.base)
        .addClass('st7');

      // draw vertical total height line + label
      this.canvas
        .line(
          10,
          heights.max - heights.base - heights.terrain,
          10,
          heights.max - heights.base,
        )
        .addClass('st7');
      if (this.showLabels) {
        this.canvas
          .text(heights.terrain + ' cm')
          .attr(
            'style',
            'font-family:"Source Sans Pro", sans-serif; font-size:7.4976px;',
          )
          .transform({
            translate: [7, heights.max / 2 - heights.base],
            rotate: 90,
            origin: 'center center',
          });
      }

      // draw vertical shaft height line + label
      this.canvas
        .line(
          35,
          heights.max - heights.shaft - heights.base,
          35,
          heights.max - heights.base,
        )
        .addClass('st7');
      if (this.showLabels) {
        this.canvas
          .text(heights.shaft + ' cm')
          .attr(
            'style',
            'font-family:"Source Sans Pro", sans-serif; font-size:7.4976px;',
          )
          .attr('fill', heights.shaft > heights.terrain ? 'red' : null)
          .transform({
            translate: [32, heights.max - (heights.shaft + heights.sole) / 2],
            rotate: 90,
            origin: 'center center',
          });
      }
    },
  },
};
</script>
